import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";

import { SidebarProvider, useSidebar } from "../../components/ui/Sidebar";
import Sidebar from "../../components/ui/Sidebar";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";

import {
  archiveBranch,
  createBranch,
  getBranches,
  updateBranch,
} from "api/branch.api";
import { getAuthenticatedUser } from "api/auth.api";
import { QUERY_KEYS } from "config/queryKeys";

const EMPTY_FORM = {
  name: "",
  code: "",
  buildingNumber: "",
  street: "",
  district: "",
  city: "",
  postalCode: "",
  countryCode: "SA",
};

const getPermissions = (authData) => {
  return (
    authData?.activeCompany?.permissions ||
    authData?.user?.activeCompany?.permissions ||
    authData?.data?.activeCompany?.permissions ||
    []
  );
};

const hasPermission = (permissions, permission) => {
  if (!Array.isArray(permissions) || permissions.length === 0) return true;
  return permissions.includes(permission);
};

const getBranchesArray = (data) => {
  const branches = data?.data?.branches || data?.branches || data?.data || [];
  return Array.isArray(branches) ? branches : [];
};

const getBranchId = (branch) => branch?._id || branch?.id;

const buildBranchPayload = (formData) => {
  const address = {
    buildingNumber: formData.buildingNumber.trim(),
    street: formData.street.trim(),
    district: formData.district.trim(),
    city: formData.city.trim(),
    postalCode: formData.postalCode.trim(),
    countryCode: formData.countryCode.trim() || "SA",
  };

  Object.keys(address).forEach((key) => {
    if (address[key] === "") delete address[key];
  });

  return {
    name: formData.name.trim(),
    ...(formData.code.trim() ? { code: formData.code.trim() } : {}),
    ...(Object.keys(address).length > 0 ? { address } : {}),
  };
};

const StatCard = ({ icon, label, value, description }) => (
  <div className="bg-card rounded-xl p-6 shadow-sm border border-border">
    <div className="flex items-center justify-between mb-4">
      <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
        <Icon name={icon} size={24} className="text-primary" />
      </div>
    </div>
    <h3 className="text-2xl font-bold text-foreground mb-1">{value}</h3>
    <p className="text-sm font-medium text-foreground">{label}</p>
    <p className="text-xs text-muted-foreground mt-1">{description}</p>
  </div>
);

const BranchModal = ({
  title,
  submitLabel,
  formData,
  setFormData,
  onClose,
  onSubmit,
  isSubmitting,
}) => {
  const { t } = useTranslation("pages");

  const updateField = (field, value) => {
    setFormData((previous) => ({ ...previous, [field]: value }));
  };

  return (
    <div className="fixed inset-0 z-[10000] bg-black/60 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">{title}</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {t("branches.formDescription")}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="p-6 space-y-5">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {t("branches.branchName")}
            </label>
            <input
              value={formData.name}
              onChange={(event) => updateField("name", event.target.value)}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              placeholder={t("branches.placeholders.branchName")}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {t("branches.branchCode")}
            </label>
            <input
              value={formData.code}
              onChange={(event) => updateField("code", event.target.value)}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              placeholder={t("branches.placeholders.branchCode")}
            />
          </div>

          <div className="border-t border-border pt-5">
            <h3 className="text-sm font-semibold text-foreground mb-4">
              {t("branches.address")}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                value={formData.buildingNumber}
                onChange={(event) =>
                  updateField("buildingNumber", event.target.value)
                }
                className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder={t("branches.placeholders.buildingNumber")}
              />
              <input
                value={formData.street}
                onChange={(event) => updateField("street", event.target.value)}
                className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder={t("branches.placeholders.street")}
              />
              <input
                value={formData.district}
                onChange={(event) =>
                  updateField("district", event.target.value)
                }
                className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder={t("branches.placeholders.district")}
              />
              <input
                value={formData.city}
                onChange={(event) => updateField("city", event.target.value)}
                className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder={t("branches.placeholders.city")}
              />
              <input
                value={formData.postalCode}
                onChange={(event) =>
                  updateField("postalCode", event.target.value)
                }
                className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder={t("branches.placeholders.postalCode")}
              />
              <input
                value={formData.countryCode}
                onChange={(event) =>
                  updateField("countryCode", event.target.value)
                }
                className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder={t("branches.placeholders.countryCode")}
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              {t("actions.cancel")}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? t("common.loading") : submitLabel}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const BranchManagementContent = () => {
  const { isCollapsed } = useSidebar();
  const { t } = useTranslation("pages");
  const queryClient = useQueryClient();

  const [search, setSearch] = React.useState("");
  const [includeArchived, setIncludeArchived] = React.useState(false);
  const [isCreateOpen, setIsCreateOpen] = React.useState(false);
  const [editingBranch, setEditingBranch] = React.useState(null);
  const [formData, setFormData] = React.useState(EMPTY_FORM);

  const branchParams = React.useMemo(
    () => ({
      ...(search.trim() ? { search: search.trim() } : {}),
      ...(includeArchived ? { includeArchived: true } : {}),
    }),
    [includeArchived, search],
  );

  const { data: authData } = useQuery({
    queryKey: QUERY_KEYS.auth.me(),
    queryFn: getAuthenticatedUser,
    retry: false,
    staleTime: 1000 * 60 * 5,
  });

  const permissions = getPermissions(authData);
  const canView = hasPermission(permissions, "branch:view");
  const canCreate = hasPermission(permissions, "branch:create");
  const canUpdate = hasPermission(permissions, "branch:update");
  const canArchive = hasPermission(permissions, "branch:archive");

  const { data, isLoading, isError, error } = useQuery({
    queryKey: QUERY_KEYS.branches.list(branchParams),
    queryFn: () => getBranches(branchParams),
    retry: false,
    enabled: canView,
  });

  const branches = getBranchesArray(data);
  const activeBranches = branches.filter(
    (branch) => !(branch?.isArchived || branch?.archivedAt),
  );
  const archivedBranches = branches.filter(
    (branch) => branch?.isArchived || branch?.archivedAt,
  );

  const invalidateBranches = () => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.branches.all });
  };

  const createMutation = useMutation({
    mutationFn: createBranch,
    onSuccess: () => {
      invalidateBranches();
      toast.success(t("branches.createdToast"));
      setIsCreateOpen(false);
      setFormData(EMPTY_FORM);
    },
    onError: (err) => {
      toast.error(err?.message || t("branches.failedCreate"));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }) => updateBranch(id, body),
    onSuccess: (_, variables) => {
      invalidateBranches();
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.branches.detail(variables.id),
      });
      toast.success(t("branches.updatedToast"));
      setEditingBranch(null);
      setFormData(EMPTY_FORM);
    },
    onError: (err) => {
      toast.error(err?.message || t("branches.failedUpdate"));
    },
  });

  const archiveMutation = useMutation({
    mutationFn: archiveBranch,
    onSuccess: () => {
      invalidateBranches();
      toast.success(t("branches.archivedToast"));
    },
    onError: (err) => {
      toast.error(err?.message || t("branches.failedArchive"));
    },
  });

  const openCreate = () => {
    setFormData(EMPTY_FORM);
    setIsCreateOpen(true);
  };

  const openEdit = (branch) => {
    setEditingBranch(branch);
    setFormData({
      name: branch?.name || "",
      code: branch?.code || "",
      buildingNumber: branch?.address?.buildingNumber || "",
      street: branch?.address?.street || "",
      district: branch?.address?.district || "",
      city: branch?.address?.city || "",
      postalCode: branch?.address?.postalCode || "",
      countryCode: branch?.address?.countryCode || "SA",
    });
  };

  const closeModal = () => {
    setIsCreateOpen(false);
    setEditingBranch(null);
    setFormData(EMPTY_FORM);
  };

  const handleCreate = (event) => {
    event.preventDefault();

    if (!formData.name.trim()) {
      toast.error(t("branches.nameRequired"));
      return;
    }

    createMutation.mutate(buildBranchPayload(formData));
  };

  const handleUpdate = (event) => {
    event.preventDefault();

    const branchId = getBranchId(editingBranch);

    if (!branchId) {
      toast.error(t("branches.couldNotIdentify"));
      return;
    }

    if (!formData.name.trim()) {
      toast.error(t("branches.nameRequired"));
      return;
    }

    updateMutation.mutate({
      id: branchId,
      body: buildBranchPayload(formData),
    });
  };

  const handleArchive = (branch) => {
    const branchId = getBranchId(branch);

    if (!branchId) {
      toast.error("Could not identify branch.");
      return;
    }

    const confirmed = window.confirm(
      `Archive branch "${branch?.name || "this branch"}"?`,
    );

    if (!confirmed) return;

    archiveMutation.mutate(branchId);
  };

  if (isLoading) {
    return <LoadingContent isCollapsed={isCollapsed} />;
  }

  const isForbidden =
    error?.status === 403 ||
    error?.response?.status === 403 ||
    error?.message?.toLowerCase?.()?.includes("permission");

  return (
    <div
      className={`transition-all duration-300 ease-in-out ${
        isCollapsed ? "md:ms-16" : "md:ms-64"
      }`}
    >
      <main className="p-4 md:p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">
                {t("branches.title")}
              </h1>
              <p className="text-muted-foreground">
                {t("branches.description")}
              </p>
            </div>

            {canCreate && (
              <Button onClick={openCreate} iconName="Plus" iconPosition="left">
                {t("actions.createBranch")}
              </Button>
            )}
          </div>
        </motion.div>

        {!canView || isError ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-card border border-border rounded-xl p-8 text-center"
          >
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon
                name={isForbidden || !canView ? "ShieldAlert" : "AlertTriangle"}
                size={32}
                className="text-muted-foreground"
              />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {isForbidden || !canView
                ? t("branches.permissionRequired")
                : t("branches.couldNotLoad")}
            </h3>
            <p className="text-muted-foreground">
              {isForbidden || !canView
                ? t("branches.permissionDescription")
                : error?.message || t("branches.failedLoad")}
            </p>
          </motion.div>
        ) : (
          <>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8"
            >
              <StatCard
                icon="Building2"
                label={t("branches.stats.total")}
                value={branches.length}
                description={t("branches.returnedDescription")}
              />
              <StatCard
                icon="CheckCircle"
                label={t("branches.stats.active")}
                value={activeBranches.length}
                description={t("branches.availableDescription")}
              />
              <StatCard
                icon="Archive"
                label={t("branches.stats.archived")}
                value={archivedBranches.length}
                description={t("branches.archivedDescription")}
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-card rounded-xl p-6 shadow-sm border border-border mb-6"
            >
              <div className="flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between">
                <div className="relative flex-1">
                  <Icon
                    name="Search"
                    size={18}
                    className="absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  />
                  <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    className="w-full rounded-lg border border-border bg-background ps-10 pe-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                    placeholder={t("branches.searchPlaceholder")}
                  />
                </div>

                <label className="flex items-center gap-2 text-sm text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={includeArchived}
                    onChange={(event) =>
                      setIncludeArchived(event.target.checked)
                    }
                  />
                  {t("branches.includeArchived")}
                </label>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              {branches.length === 0 ? (
                <div className="bg-card border border-border rounded-xl p-12 text-center">
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon
                      name="Building2"
                      size={32}
                      className="text-muted-foreground"
                    />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    {t("branches.emptyTitle")}
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    {t("branches.createdDescription")}
                  </p>
                  {canCreate && (
                    <Button
                      onClick={openCreate}
                      iconName="Plus"
                      iconPosition="left"
                    >
                      {t("actions.createFirstBranch")}
                    </Button>
                  )}
                </div>
              ) : (
                <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
                  <div className="p-6 border-b border-border">
                    <h2 className="text-lg font-semibold text-foreground">
                      {t("branches.branchesTitle")}
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      {t("branches.viewDescription")}
                    </p>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50 text-muted-foreground">
                        <tr>
                          <th className="text-start px-6 py-3 font-medium">
                            {t("branches.branch")}
                          </th>
                          <th className="text-start px-6 py-3 font-medium">
                            {t("branches.code")}
                          </th>
                          <th className="text-start px-6 py-3 font-medium">
                            {t("branches.city")}
                          </th>
                          <th className="text-start px-6 py-3 font-medium">
                            {t("branches.district")}
                          </th>
                          <th className="text-start px-6 py-3 font-medium">
                            {t("branches.postalCode")}
                          </th>
                          <th className="text-start px-6 py-3 font-medium">
                            {t("status.status")}
                          </th>
                          <th className="text-start px-6 py-3 font-medium">
                            {t("branches.created")}
                          </th>
                          <th className="text-end px-6 py-3 font-medium">
                            {t("invoices.table.actions")}
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {branches.map((branch) => {
                          const branchId = getBranchId(branch);
                          const isArchived = Boolean(
                            branch?.isArchived || branch?.archivedAt,
                          );

                          return (
                            <tr
                              key={branchId}
                              className="border-t border-border hover:bg-muted/30 transition-colors"
                            >
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                                    <Icon
                                      name="Building2"
                                      size={18}
                                      className="text-primary"
                                    />
                                  </div>
                                  <div>
                                    <p className="font-medium text-foreground">
                                      {branch?.name || t("branches.unnamed")}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      {branch?.address?.street || t("branches.noStreet")}
                                    </p>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 text-muted-foreground">
                                {branch?.code || "—"}
                              </td>
                              <td className="px-6 py-4 text-muted-foreground">
                                {branch?.address?.city || "—"}
                              </td>
                              <td className="px-6 py-4 text-muted-foreground">
                                {branch?.address?.district || "—"}
                              </td>
                              <td className="px-6 py-4 text-muted-foreground">
                                {branch?.address?.postalCode || "—"}
                              </td>
                              <td className="px-6 py-4">
                                <span
                                  className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${
                                    isArchived
                                      ? "bg-muted text-muted-foreground"
                                      : "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                                  }`}
                                >
                                  {isArchived ? t("status.archived") : t("status.active")}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-muted-foreground">
                                {branch?.createdAt
                                  ? new Date(
                                      branch.createdAt,
                                    ).toLocaleDateString()
                                  : "—"}
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex justify-end gap-2">
                                  {canUpdate && !isArchived && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => openEdit(branch)}
                                    >
                                      {t("actions.edit")}
                                    </Button>
                                  )}
                                  {canArchive && !isArchived && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleArchive(branch)}
                                      disabled={archiveMutation.isPending}
                                    >
                                      {t("actions.archive")}
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </motion.div>
          </>
        )}

        {isCreateOpen && (
          <BranchModal
            title={t("actions.createBranch")}
            submitLabel={t("actions.createBranch")}
            formData={formData}
            setFormData={setFormData}
            onClose={closeModal}
            onSubmit={handleCreate}
            isSubmitting={createMutation.isPending}
          />
        )}

        {editingBranch && (
          <BranchModal
            title={t("branches.editTitle")}
            submitLabel={t("actions.saveChanges")}
            formData={formData}
            setFormData={setFormData}
            onClose={closeModal}
            onSubmit={handleUpdate}
            isSubmitting={updateMutation.isPending}
          />
        )}
      </main>
    </div>
  );
};

const LoadingContent = ({ isCollapsed }) => (
  <div
    className={`transition-all duration-300 ease-in-out ${
      isCollapsed ? "md:ms-16" : "md:ms-64"
    }`}
  >
    <main className="p-4 md:p-6">
      <div className="animate-pulse">
        <div className="mb-8">
          <div className="h-8 bg-muted rounded w-72 mb-2"></div>
          <div className="h-4 bg-muted rounded w-96"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="bg-card rounded-xl p-6 shadow-sm border border-border"
            >
              <div className="h-12 bg-muted rounded mb-4"></div>
              <div className="h-6 bg-muted rounded mb-2"></div>
              <div className="h-4 bg-muted rounded w-24"></div>
            </div>
          ))}
        </div>

        <div className="bg-card rounded-xl p-6 shadow-sm border border-border mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="h-10 bg-muted rounded flex-1"></div>
            <div className="h-10 bg-muted rounded w-40"></div>
          </div>
        </div>

        <div className="bg-card rounded-xl shadow-sm border border-border">
          <div className="p-6">
            <div className="h-6 bg-muted rounded w-48 mb-4"></div>
            <div className="space-y-3">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="flex items-center gap-4">
                  <div className="h-10 w-10 bg-muted rounded"></div>
                  <div className="h-4 bg-muted rounded w-36"></div>
                  <div className="h-4 bg-muted rounded w-24"></div>
                  <div className="h-4 bg-muted rounded w-28"></div>
                  <div className="h-4 bg-muted rounded w-20"></div>
                  <div className="h-4 bg-muted rounded w-20"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
);

const BranchManagement = () => {
  return (
    <SidebarProvider>
      <div className="min-h-screen bg-background">
        <Sidebar />
        <BranchManagementContent />
      </div>
    </SidebarProvider>
  );
};

export default BranchManagement;
